import { useConfirmPatientAppointment } from '@/api/hooks/useConfirmPatientAppointment'
import type { UpcomingPatientAppointment } from '@/api/types/ProviderApi'
import { HighlightText } from '@/components/HighlightText'
import { RescheduleConfirmationModal } from '@/components/RescheduleConfirmationModal'
import SuccessCheckmark from '@/components/svgs/SuccessCheckmark'
import { analytics } from '@/lib/analytics'
import { Button, ButtonText } from '@/ui/core/button'
import { Header } from '@/ui/core/header'
import { ExternalLink } from '@/ui/core/link'
import { HStack, VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { Link } from 'expo-router'
import { useMemo } from 'react'
import Toast from 'react-native-toast-message'

const LOCATION_ID_TO_MAP: Record<number, string> = {
	1: 'https://maps.app.goo.gl/bYCe4Du9mE8NajMR9',
	2: 'https://maps.app.goo.gl/ytHvPwTwywbuMg3RA',
	3: 'https://maps.app.goo.gl/conGEfYsnRrgamVo7',
}

const CONFIRM_ERROR_TOAST_CONFIG = {
	type: 'error',
	text1: 'Failed to confirm appointment',
	text2: 'Please try again',
}

const CONFIRM_SUCCESS_TOAST_CONFIG = {
	type: 'success',
	text2: 'Appointment confirmed!',
}

const RescheduleAppointmentButton = ({
	appointment,
}: { appointment: UpcomingPatientAppointment }) => {
	const isPerioAppointment = useMemo(
		() => appointment.types.find((type) => type === 'PERIO_THERAPY'),
		[appointment]
	)

	const buttonVariant = appointment?.isConfirmable ? 'ghostBorderless' : 'ghost'

	if (isPerioAppointment) {
		return (
			<Link
				asChild
				href={{
					pathname: '/reschedule-perio-appointment',
					params: {
						appointmentId: appointment.appointmentId,
					},
				}}
			>
				<Button variant={buttonVariant}>
					<ButtonText spacing="wide" className="px-2 text-sm font-primary-bold">
						RESCHEDULE
					</ButtonText>
				</Button>
			</Link>
		)
	}

	return (
		<RescheduleConfirmationModal appointmentId={appointment.appointmentId}>
			<Button variant="ghost">
				<ButtonText spacing="wide" className="text-sm font-primary-bold">
					RESCHEDULE
				</ButtonText>
			</Button>
		</RescheduleConfirmationModal>
	)
}

export const UpcomingAppointment = ({
	appointment,
}: { appointment: UpcomingPatientAppointment }) => {
	const { mutate: confirmAppointment, isPending: isConfirmingAppointment } =
		useConfirmPatientAppointment()

	const onConfirmButtonPress = () => {
		analytics.trackClick('confirm-appointment', {
			appointmentId: appointment.appointmentId,
			appointmentTypes: appointment.types.join(','),
		})

		confirmAppointment(
			{ appointmentId: appointment.appointmentId },
			{
				onSuccess: () => Toast.show(CONFIRM_SUCCESS_TOAST_CONFIG),
				onError: () => Toast.show(CONFIRM_ERROR_TOAST_CONFIG),
			}
		)
	}

	return (
		<>
			<HighlightText>Upcoming appointment</HighlightText>
			<VStack className="items-center" gap={6}>
				<Header
					variant="h2"
					className="lg:max-w-full max-w-80 text-balance text-center leading-tight"
				>
					{appointment?.startTimeFormatted}
				</Header>
				<ExternalLink href={LOCATION_ID_TO_MAP[appointment?.locationId || 1]}>
					{appointment?.address}
				</ExternalLink>
			</VStack>
			{!appointment.isConfirmable && !appointment.isConfirmed && (
				<RescheduleAppointmentButton appointment={appointment} />
			)}
			{appointment?.isConfirmable && (
				<VStack gap={8} className="items-center justify-center">
					<Button isLoading={isConfirmingAppointment} onPress={onConfirmButtonPress}>
						<ButtonText spacing="wide" className="px-2 text-sm font-primary-bold">
							CONFIRM
						</ButtonText>
					</Button>
					<RescheduleAppointmentButton appointment={appointment} />
				</VStack>
			)}
			{appointment.isConfirmed && (
				<HStack gap={8} className="items-center justify-center">
					<SuccessCheckmark />
					<Text className="text-palette_6" weight="bold" spacing="wide">
						CONFIRMED
					</Text>
				</HStack>
			)}
		</>
	)
}
